<script setup lang="ts">
import { ref, watch, onUnmounted, computed } from 'vue';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { useDevice } from '@/stores/device.store';
import { GetManyResult, IPrinter } from '@posx/core';
import { usePrinterStore } from '@/stores/printer';
import { ISystemDevice } from '../types/device.type';
import { useI18n } from 'vue-i18n';
import {
  Rocket,
  Upgrade,
  CurrentDevice,
  ReplayIcon
} from '@xpos/icons-svg';
import packageConfig from '@/../package.json';
import { useUpdateStore } from '@/stores/update';
import UpgradeDialog from '@/components/UpgradeDialog.vue';

const { t } = useI18n();
const $emit = defineEmits(['change', 'update:modelValue', 'close']);
const $props = defineProps({
  modelValue: {
    type: Boolean,
  },
});

const visible = ref($props.modelValue);
let timer: undefined | NodeJS.Timeout = undefined;
const timeout = ref<number>(0);
const loading = ref(false);

const deviceStore = useDevice();
const printerStore = usePrinterStore();
const updateStore = useUpdateStore();

const currentDevice = ref(secureStorage.getItem('device_uid'));
console.log('currentDevice', currentDevice.value);
const uuid = secureStorage.getItem('deviceId');
console.log('uuid', uuid);

// 从package.json获取当前版本
const currentVersion = packageConfig.version;
// 从updateStore获取最新版本
const latestVersion = computed(() => updateStore.updateInfo.version || currentVersion);

const hasNewVersion = computed(() => {
  return updateStore.hasUpdate;
});

// 添加升级对话框控制
const showUpgradeDialog = ref(false);

const getColor = (status: string) => {
  switch (status) {
    case '1':
      return '#80BA5C';
    case '2':
      return '#E53030';
    case '3':
      return '#52599D';
  }
};

const devices = ref<Partial<ISystemDevice>[]>([]);
const printers = ref<GetManyResult<IPrinter>>({
  list: [],
  count: 0,
  page: 0,
  pageSize: 0,
});
const getData = async () => {
  if (loading.value) return;
  loading.value = true;
  devices.value = await deviceStore.getDevices();
  printers.value = await printerStore.getMany({});
  loading.value = false;
  if (devices.value && devices.value.length > 0) {
    devices.value.sort((a, b) => (a.is_main ? -1 : 0));
  }
  handleRefresh();
};

const handleRefresh = () => {
  if (!timer && $props.modelValue) { // don't re-arm if dialog closed
    getData();
    timeout.value = 5;
    timer = setInterval(() => {
      if (timeout.value === 0) {
        clearInterval(timer);
        timer = undefined;
        getData();
      } else {
        timeout.value--;
      }
    }, 1000);
  }
};

const handleClose = () => {
  $emit('change', false);
  $emit('update:modelValue', false);
  $emit('close', false);
};

onUnmounted(() => {
  timer && clearInterval(timer);
  timer = undefined;
  loading.value = false;
});

watch(
  () => $props.modelValue,
  async (newVal) => {
    visible.value = newVal;
    if (newVal) {
      // 控制中心打开时检查更新
      await updateStore.checkForUpdates();
      handleRefresh();
    } else {
      timer && clearInterval(timer);
      timer = undefined;
    }
  },
);
</script>

<template>
  <x-dialog
    v-model="visible"
    width="770px"
    :title="t('deviceList.title')"
    :before-close="handleClose"
    @close="handleClose"
    :close-on-click-modal="false"
  >
    <div class="text-left w-[770px] flex flex-col justify-between">
      <div class="mb-[10px] text-sm font-medium text-[#3A3A3A]">{{ t('deviceList.upgrade') }}</div>
      <div>
        <div class="bg-[#F6F6FA] rounded-lg p-4 mb-4 shadow-sm">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-4">
                <el-icon size="60">
                    <Rocket v-if="hasNewVersion" />
                    <Upgrade v-else />
                </el-icon>
              <div>
                <div class="text-base">{{ t('deviceList.current_version') }} Ver.{{ currentVersion }}</div>
                <div class="text-[#6B7AE5]" v-if="hasNewVersion">{{ t('deviceList.upgrade_available') }}</div>
                <div class="text-[#A8A8A8]" v-else>{{ t('deviceList.upgrade_available_no') }}</div>
                <div class="text-[#6B7AE5]" v-if="hasNewVersion">Ver.{{ latestVersion }}</div>
              </div>
            </div>
            <x-button
              v-if="hasNewVersion"
              type="primary"
              class="!bg-[#80BA5C] !border-[#80BA5C] w-[200px] h-[50px] rounded-[10px]"
              @click="showUpgradeDialog = true"
            >
                {{ t('deviceList.upgrade_now') }}
            </x-button>
          </div>
        </div>
        <!-- <div class="mb-[10px] text-sm font-medium text-[#3A3A3A]">{{ t('deviceList.currentDevice') }}</div>
        <div class="mb-[10px]">
          <x-button square size="large" :color="getColor(currentDevice == uuid ? '3' : '1')">
            <div class="text-white h-full flex flex-col justify-between items-center">
              <img src="/device_ipad.png" class="w-[50px] mt-[15px]" />
              <div class="text-xs">
                {{ currentDevice == uuid ? t('deviceList.mainDevice') : t('deviceList.subDevice') }}
              </div>
            </div>
          </x-button>
        </div> -->
        <div class="mb-[10px] text-sm font-medium text-[#3A3A3A]">{{ t('deviceList.printer') }}</div>
        <div class="mb-[10px] flex gap-[5px] flex-wrap">
          <div v-for="item in printers?.list">
            <x-button square size="large" :color="getColor(item.status === 'online' ? '1' : '2')">
              <div class="text-white h-full flex flex-col justify-between items-center">
                <img src="/device_printer.png" class="w-[50px] mt-[15px]" />
                <div class="text-xs">
                  <div>{{ t('setup.printers.type.' + item.type) }}</div>
                  <div>{{ item.ip_address }}</div>
                </div>
              </div>
            </x-button>
          </div>
        </div>
        <div class="mb-[10px] text-sm font-medium text-[#3A3A3A]">{{ t('deviceList.device') }}</div>
        <div class="mb-[10px] flex gap-[5px] flex-wrap">
          <div v-for="device in devices">
            <x-button square size="large" :color="getColor(device.is_main ? '3' : '1')">
              <div class="text-white h-full flex flex-col justify-between items-center relative w-full">
                <div v-if="currentDevice === device.uid" class="absolute top-[-2px] right-[-5px]">
                  <el-icon size="26">
                    <CurrentDevice />
                  </el-icon>
                </div>
                <img src="/device_ipad.png" class="w-[50px] mt-[15px]" />
                <div class="text-xs">
                  {{ device.is_main ? t('deviceList.mainDevice') : t('deviceList.subDevice') }}
                </div>
              </div>
            </x-button>
          </div>
        </div>
      </div>
      <div>
        <x-button type="primary" :loading="loading" @click="getData">
          <div>
            <el-icon size="20" class="align-text-bottom">
              <ReplayIcon />
            </el-icon>
            {{ t('deviceList.refresh') }} {{ '(' + timeout + 's)' }}
          </div>
        </x-button>
      </div>
    </div>
  </x-dialog>
  
  <UpgradeDialog v-if="showUpgradeDialog" v-model="showUpgradeDialog" :upgrade-info="updateStore.updateInfo" :is-force="false" :auto-update="true" />
</template>
